import { useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { Project } from "@/api/types";
import { useWorkspaceId, useSession } from "@/app/session";
import { queryClient } from "@/app/query";
import { useMembers } from "@/features/data";
import { Page } from "@/components/Page";
import { Card, ColorDot, ErrorState, IconButton, Spinner } from "@/components/ui";
import { Select } from "@/components/Select";
import { ProjectPicker } from "@/components/ProjectPicker";
import { useToast, errorMessage } from "@/components/Toast";
import { secondsToHMM } from "@/lib/format";
import { isoDate, isoWeek, fromISODate } from "@/lib/dates";
import { cn } from "@/lib/cn";

interface Cell { seconds: number; entry_count: number; editable: boolean; locked: boolean }
interface Row {
  project_id: string | null;
  project_name: string | null;
  project_color: string | null;
  client_name: string | null;
  task_id: string | null;
  task_name: string | null;
  days: Cell[];
  total: number;
}
interface TimesheetDTO { week: string; dates: string[]; rows: Row[]; day_totals: number[]; total: number }

/** parseDuration accepts "1:30", "1.5" (hours) or "90m"; returns seconds (0 clears). */
function parseDuration(input: string): number | null {
  const s = input.trim();
  if (s === "") return 0;
  if (s.includes(":")) {
    const [h, m] = s.split(":");
    const hh = Number(h), mm = Number(m);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    return hh * 3600 + mm * 60;
  }
  if (s.endsWith("m")) {
    const mm = Number(s.slice(0, -1));
    return Number.isNaN(mm) ? null : mm * 60;
  }
  const hrs = Number(s);
  if (Number.isNaN(hrs)) return null;
  return Math.round(hrs * 3600);
}

export function TimesheetPage() {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const { workspace } = useSession();
  const isAdmin = workspace?.role !== "member";
  const { data: members = [] } = useMembers();
  const [userId, setUserId] = useState("");
  const [anchor, setAnchor] = useState(() => new Date());
  const [extra, setExtra] = useState<Row[]>([]);
  const [editing, setEditing] = useState<{ key: string; day: number } | null>(null);
  const { year, week } = isoWeek(anchor);
  const weekSpec = `${year}-W${String(week).padStart(2, "0")}`;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["timesheet", wid, weekSpec, userId],
    queryFn: () => api.get<TimesheetDTO>(`/workspaces/${wid}/timesheet?week=${weekSpec}${userId ? `&user_id=${userId}` : ""}`),
  });

  const shift = (days: number) => {
    setAnchor((a) => { const n = new Date(a); n.setDate(n.getDate() + days); return n; });
    setExtra([]);
  };
  const goToday = () => { setAnchor(new Date()); setExtra([]); };

  async function saveCell(row: Row, dayIndex: number, text: string) {
    setEditing(null);
    const seconds = parseDuration(text);
    if (seconds === null) { toast("Enter hours like 1:30 or 1.5", "error"); return; }
    try {
      await api.put(`/workspaces/${wid}/timesheet/cell`, {
        user_id: userId || undefined,
        project_id: row.project_id,
        task_id: row.task_id,
        date: data!.dates[dayIndex],
        seconds,
      });
      void queryClient.invalidateQueries({ queryKey: ["timesheet", wid, weekSpec] });
      void queryClient.invalidateQueries({ queryKey: ["entries", wid] });
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  function addRow(p: Project) {
    const key = `${p.id} `;
    const exists = (data?.rows ?? []).some((r) => `${r.project_id} ${r.task_id ?? ""}`.startsWith(key)) || extra.some((r) => r.project_id === p.id);
    if (exists) return;
    setExtra((rows) => [...rows, {
      project_id: p.id, project_name: p.name, project_color: p.color, client_name: null, task_id: null, task_name: null,
      days: Array.from({ length: 7 }, () => ({ seconds: 0, entry_count: 0, editable: true, locked: false })),
      total: 0,
    }]);
  }

  const rows = [...(data?.rows ?? []), ...extra.filter((x) => !(data?.rows ?? []).some((r) => r.project_id === x.project_id && r.task_id == null))];
  const todayIso = isoDate(new Date());
  const rangeLabel = data
    ? `${fromISODate(data.dates[0]).toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${fromISODate(data.dates[6]).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`
    : "";

  return (
    <Page
      title="Timesheet"
      wide
      actions={
        <div className="flex items-center gap-2">
          {isAdmin && members.length > 1 && (
            <Select value={userId} onChange={(e) => { setUserId(e.target.value); setExtra([]); }} aria-label="View timesheet for member" className="h-8 text-xs">
              <option value="">My timesheet</option>
              {members.filter((m) => !m.self).map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
            </Select>
          )}
          <button onClick={goToday} className="rounded-md border border-line px-3 py-1 text-[13px] text-muted hover:text-ink">This week</button>
          <IconButton label="Previous week" onClick={() => shift(-7)}><ChevronLeft className="size-4" /></IconButton>
          <span className="min-w-40 text-center text-sm font-medium">
            {rangeLabel} <span className="mono text-xs text-faint">· W{week}</span>
          </span>
          <IconButton label="Next week" onClick={() => shift(7)}><ChevronRight className="size-4" /></IconButton>
        </div>
      }
    >
      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isLoading || !data ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-xs text-faint">
                <th scope="col" className="px-4 py-2.5 text-left font-medium">Project / Task</th>
                {data.dates.map((d) => {
                  const day = fromISODate(d);
                  const isToday = d === todayIso;
                  return (
                    <th scope="col" key={d} className={cn("px-2 py-2.5 text-center font-medium", isToday && "bg-elevated/40")}>
                      <span className={cn(isToday && "text-accent")}>{day.toLocaleDateString(undefined, { weekday: "short" })}</span>
                      <br />
                      <span className={cn("mono", isToday ? "text-accent" : "text-faint")}>{day.getDate()}</span>
                    </th>
                  );
                })}
                <th scope="col" className="px-4 py-2.5 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-faint">No time this week. Add a row to fill it in.</td></tr>
              )}
              {rows.map((r) => {
                const key = `${r.project_id} ${r.task_id ?? ""}`;
                return (
                  <tr key={key} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-2 text-ink">
                        <ColorDot color={r.project_color ?? "#888"} />
                        {r.project_name ?? "No project"}
                        {r.task_name && <span className="text-faint">· {r.task_name}</span>}
                      </span>
                    </td>
                    {r.days.map((c, ci) => {
                      const isEditing = editing?.key === key && editing.day === ci;
                      const cellText = c.seconds > 0 ? secondsToHMM(c.seconds) : "";
                      const isToday = data.dates[ci] === todayIso;
                      return (
                        <td key={ci} className={cn("px-1 py-1 text-center", isToday && "bg-elevated/30")}>
                          {isEditing ? (
                            <input
                              autoFocus
                              defaultValue={cellText}
                              onBlur={(e) => saveCell(r, ci, e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setEditing(null); }}
                              className="mono h-8 w-16 rounded border border-accent bg-canvas text-center text-sm tabular-nums text-ink focus:outline-none"
                            />
                          ) : (
                            <button
                              disabled={!c.editable}
                              onClick={() => c.editable && setEditing({ key, day: ci })}
                              title={c.locked ? "Closed month" : c.editable ? "" : "Multiple entries — edit in Tracker"}
                              className={cn(
                                "group/cell mono h-8 w-16 rounded text-sm tabular-nums",
                                c.editable ? "text-ink hover:bg-elevated" : "cursor-not-allowed text-faint",
                              )}
                            >
                              {c.seconds > 0 ? (
                                secondsToHMM(c.seconds)
                              ) : c.editable ? (
                                <>
                                  <span className="group-hover/cell:hidden">·</span>
                                  <span className="hidden text-accent group-hover/cell:inline">+</span>
                                </>
                              ) : (
                                "·"
                              )}
                            </button>
                          )}
                        </td>
                      );
                    })}
                    <td className="mono px-4 py-2 text-right font-medium tabular-nums text-ink">{secondsToHMM(r.total)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-line-strong font-medium">
                <td className="px-4 py-2.5 text-ink">Total</td>
                {data.day_totals.map((t, i) => (
                  <td key={i} className={cn("mono px-2 py-2.5 text-center tabular-nums text-ink", data.dates[i] === todayIso && "bg-elevated/30")}>
                    {t > 0 ? secondsToHMM(t) : "·"}
                  </td>
                ))}
                <td className="mono px-4 py-2.5 text-right tabular-nums text-ink">{secondsToHMM(data.total)}</td>
              </tr>
            </tfoot>
          </table>
          <div className="border-t border-line p-2">
            <ProjectPicker value={null} onChange={(_id, p) => p && addRow(p)} compact />
            <span className="ml-2 text-xs text-faint"><Plus className="inline size-3" /> add a project row, then click a cell to enter hours (e.g. 1:30 or 1.5)</span>
          </div>
        </Card>
      )}
    </Page>
  );
}
